import { Box, Stack, Typography } from "@mui/material"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"
import { MOTION, transition } from "../theme"

/** The server's minimum. Mirrored so the client can say so before the round trip. */
export const MIN_PASSWORD_LENGTH = 10

/**
 * A handful of passwords that are tried first on every account in the world.
 *
 * Not a blocklist — the real ones run to hundreds of millions of entries and
 * belong on the server, where a rejection can actually be enforced. This is the
 * shortest list that makes the fourth check mean something rather than reward
 * anybody who types ten characters of anything.
 */
const COMMON = [
  "password",
  "passw0rd",
  "letmein",
  "welcome",
  "qwerty",
  "iloveyou",
  "monkey",
  "dragon",
  "football",
  "baseball",
  "sunshine",
  "princess",
  "admin",
  "login",
  "abc123",
  "changeme",
]

function looksCommon(value) {
  // A trailing run of digits is the usual way a common word is stretched to
  // length — `password1`, `welcome2024` — so it is stripped before comparing.
  const stem = value.toLowerCase().replace(/[^a-z]+$/g, "")
  if (COMMON.includes(stem)) return true
  if (COMMON.some((word) => stem.startsWith(word) && stem.length - word.length <= 2)) return true
  // One character repeated, or a single character class typed straight through.
  if (/^(.)\1+$/.test(value)) return true
  return false
}

/**
 * The four things being asked for, and whether each is met.
 *
 * Exported because the submit button's enabled state is the same question, and
 * two implementations of "is this password acceptable" drift within a release.
 */
export function passwordChecks(value) {
  const password = String(value || "")
  return [
    { id: "length", label: `At least ${MIN_PASSWORD_LENGTH} characters`, ok: password.length >= MIN_PASSWORD_LENGTH },
    { id: "number", label: "A number", ok: /\d/.test(password) },
    { id: "letter", label: "A letter", ok: /[a-zA-Z]/.test(password) },
    {
      id: "uncommon",
      label: "Not a word you use elsewhere",
      ok: password.length >= MIN_PASSWORD_LENGTH && !looksCommon(password),
    },
  ]
}

export function passwordAccepted(value) {
  return passwordChecks(value).every((check) => check.ok)
}

const TONES = [
  { color: "error.main", word: "Too weak" },
  { color: "error.main", word: "Too weak" },
  { color: "warning.main", word: "Weak" },
  { color: "info.main", word: "Fine" },
  { color: "success.dark", word: "Strong" },
]

/**
 * Four segments and a live checklist.
 *
 * A bare meter tells somebody their password is weak and leaves them guessing
 * what would fix it, so they add an exclamation mark and try again. The
 * checklist answers the question the meter raises, and it answers it while they
 * are still typing rather than after a failed submit.
 *
 * The segments are not a strength estimate dressed up as one — they count the
 * four checks below them, so the bar and the list can never disagree. Anything
 * claiming to measure entropy from a text field is guessing, and a meter that
 * calls `Tr0ub4dor&3` strong and `correct horse battery staple` weak has taught
 * somebody the wrong lesson with great confidence.
 */
export default function PasswordStrength({ value, sx }) {
  const password = String(value || "")
  const checks = passwordChecks(password)
  const score = checks.filter((c) => c.ok).length
  const tone = TONES[score]
  const showWord = password.length > 0

  return (
    <Box sx={{ mt: 1.25, ...sx }}>
      <Stack direction="row" spacing="4px" aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: 4,
              borderRadius: "2px",
              bgcolor: password && i < score ? tone.color : "action.selected",
              transition: transition("background-color", MOTION.base),
            }}
          />
        ))}
      </Stack>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", mt: 1 }}>
        {/* Full width so the checklist always starts on the line below it —
            otherwise the first check wraps up beside the verdict and the two
            read as one sentence. */}
        {showWord ? (
          <Typography variant="caption" sx={{ width: "100%", fontSize: 12, fontWeight: 600, color: tone.color }}>
            {tone.word}
          </Typography>
        ) : null}

        {checks.map((check) => (
          <Box key={check.id} sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
            {check.ok ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "success.dark" }} />
            ) : (
              <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: "divider" }} />
            )}
            <Typography variant="caption" sx={{ fontSize: 12, color: "text.secondary" }}>
              {check.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
